import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import * as S from './OperatorSelect.styled';
import { useResponsive } from '@/hooks/useResponsive';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { useScrollToSelectedOnOpen } from '@/hooks/useScrollToSelectedOnOpen';
import {
  GET_FORM_ELEMENTS,
  GetFormElementsResponse,
} from '@/features/PhoneNumberSearch/api';

const SKELETON_COUNT = 10;

type OperatorSelectProps = {
  value: string | null;
  onChange: (value: string | null) => void;
  isHoverSuppressed: boolean;
  setIsHoverSuppressed: (value: boolean) => void;
};

export const OperatorSelect = ({
  value,
  onChange,
  isHoverSuppressed = false,
  setIsHoverSuppressed,
}: OperatorSelectProps) => {
  const { t } = useTranslation();
  const { isDesktop } = useResponsive();
  const [isOpen, toggle] = useToggleOpenState();
  const listRef = useRef<HTMLDivElement>(null);

  const { data, loading } = useQuery<GetFormElementsResponse>(
    GET_FORM_ELEMENTS,
    { skip: !isOpen }
  );

  const isLoading = loading && !data;

  useScrollToSelectedOnOpen(isOpen && !isLoading, listRef);

  const operatorCodes =
    data?.formElements?.phoneNumberSearchOperatorCodes ?? [];
  const options = [
    {
      label: t('pages.ads.phone_number_search.operator_any'),
      value: null,
    },
    ...operatorCodes.map(code => ({ label: code, value: code })),
  ];

  function handleSelect(nextValue: string | null) {
    onChange(nextValue);
    toggle(false);
  }

  function handlePointerLeave(event: React.PointerEvent<HTMLDivElement>) {
    if (event.pointerType === 'mouse') {
      setIsHoverSuppressed(false);
    }
  }

  const renderOptions = () =>
    isLoading
      ? Array.from({ length: SKELETON_COUNT }, (_, index) => (
          <S.OptionSkeletonRow key={index}>
            <S.OptionSkeleton />
          </S.OptionSkeletonRow>
        ))
      : options.map(({ label, value: optionValue }) => {
          const isSelected = optionValue === value;
          return isDesktop ? (
            <S.MenuItem
              key={label}
              data-selected={isSelected}
              onClick={() => handleSelect(optionValue)}
            >
              <S.MenuItemText>{label}</S.MenuItemText>
              <S.RadioButton $isActive={isSelected} />
            </S.MenuItem>
          ) : (
            <S.Option
              key={label}
              label={label}
              value={isSelected}
              onChangeHook={() => handleSelect(optionValue)}
            />
          );
        });

  return (
    <S.Container>
      <S.Trigger
        $isActive={isOpen}
        $isHoverSuppressed={isHoverSuppressed}
        onClick={toggle}
        onPointerLeave={handlePointerLeave}
      >
        <S.TriggerLabel $isPlaceholder={!value}>
          {value ?? '***'}
        </S.TriggerLabel>
        <S.DropdownIcon />
      </S.Trigger>
      {isDesktop ? (
        <S.Dropdown isOpen={isOpen} onClose={() => toggle(false)}>
          <S.Menu ref={listRef}>{renderOptions()}</S.Menu>
        </S.Dropdown>
      ) : (
        <S.Sheet
          title={t('pages.ads.phone_number_search.operator_code_title')}
          isOpen={isOpen}
          close={() => toggle(false)}
        >
          <S.SheetContent>
            <S.OptionsList ref={listRef}>{renderOptions()}</S.OptionsList>
          </S.SheetContent>
        </S.Sheet>
      )}
    </S.Container>
  );
};
